import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useNavigate } from "react-router-dom";
import { CustomSelect, SearchInput, SubHeading } from "../shared/Components";
import Icon from "../assets/icons/Icon";
import { Tab, Tabs } from "../shared/Components/UI/CustomTabs";
import { API_GET_DASHBOARD } from "../utils/api/ApiConstant";
import { useQuery } from "@tanstack/react-query";
import { getAxios } from "../shared/Services/commonService";
import DateUtils from "../utils/dateUtils";
import { selectDashboardEventsByType } from "../store/user/game-selector";
import { SPORTS_ICON } from "../utils/iconUtil";
import { dispatch } from "../store";
import { dashboardActions } from "../store/dashboard/dashboard-slice";
import MatchOdds from "./MatchOdds";
import useDashboardSocket from "../shared/Hooks/useDashboardSocket";

const GAME_FILTER_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Today", value: "Today" },
  { label: "Tomorrow", value: "Tomorrow" },
  { label: "Future", value: "Future" },
];

const Dashboard = () => {
  const [activeKey, setActiveKey] = useState(null);
  const [filterDate, setFilterDate] = useState({
    fromDate: null,
    toDate: null,
  });

  const [filterParams, setFilterParams] = useState("All");
  const [searchText, setSearchText] = useState("");

  const [isPending, startTransition] = useTransition();
  const navigate = useNavigate();

  const handleSearch = useCallback(
    (value) => {
      startTransition(() => {
        setSearchText(value);
      });
    },
    [startTransition],
  );

  // Fetch data from API
  const { data: dashboardInitialData } = useQuery({
    queryKey: ["dashboardInitialData", filterDate],
    queryFn: () =>
      getAxios(API_GET_DASHBOARD, {
        fromDate: filterDate.fromDate,
        toDate: filterDate.toDate,
      }),
  });

  useEffect(() => {
    const today = new Date();

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const future = new Date(today);
    future.setDate(today.getDate() + 2);

    const todayUTC = DateUtils.toUTC(today);
    const tomorrowUTC = DateUtils.toUTC(tomorrow);
    const futureUTC = DateUtils.toUTC(future);

    if (filterParams === "Today") {
      setFilterDate({
        fromDate: todayUTC,
        toDate: todayUTC,
      });
    } else if (filterParams === "Tomorrow") {
      setFilterDate({
        fromDate: tomorrowUTC,
        toDate: tomorrowUTC,
      });
    } else if (filterParams === "Future") {
      setFilterDate({
        fromDate: futureUTC,
        toDate: null,
      });
    } else {
      setFilterDate({
        fromDate: null,
        toDate: null,
      });
    }
  }, [filterParams]);

  // First Tab show active by default
  useEffect(() => {
    if (dashboardInitialData?.eventTypes?.length > 0) {
      setActiveKey(Number(dashboardInitialData.eventTypes[0].eventTypeId));
    }
  }, [dashboardInitialData?.eventTypes]);

  useEffect(() => {
    if (!dashboardInitialData?.events) return;

    const newObj = {};

    dashboardInitialData?.events.forEach((element) => {
      if (element?.marketId) {
        newObj[element.marketId] = element;
      }
    });

    dispatch(dashboardActions.setInitialFeed(newObj));
  }, [dashboardInitialData?.events]);

  const marketIds = useMemo(() => {
    if (!dashboardInitialData?.events) return "";

    return dashboardInitialData.events.map((x) => x.marketId).join(",");
  }, [dashboardInitialData?.events]);

  useDashboardSocket(marketIds);

  const handleTabChange = useCallback((k) => {
    setActiveKey(Number(k));
  }, []);

  // const handleTabChange = useCallback((k) => {
  //   startTransition(() => {
  //     setActiveKey(Number(k));
  //   });
  // }, []);

  const selectedEventData = useMemo(
    () => selectDashboardEventsByType({}, dashboardInitialData, activeKey),
    [activeKey, dashboardInitialData],
  );
  const filteredData = useMemo(() => {
    const search = searchText?.toLowerCase()?.trim();

    return selectedEventData
      ?.filter((game) => {
        if (!search) return true;

        return (
          game.eventName?.toLowerCase()?.includes(search) ||
          game.competitionName?.toLowerCase()?.includes(search) ||
          game.eventTypeName?.toLowerCase()?.includes(search)
        );
      })
      ?.map((game) => {
        const teams = game.eventName?.split(/ v | vs /i) || [];

        return {
          ...game,
          firstLetter: teams[0]?.trim()?.charAt(0)?.toUpperCase() || "",
          secondLetter: teams[1]?.trim()?.charAt(0)?.toUpperCase() || "",

          formattedDate: DateUtils.fromUTC(game.eventOpenOn),
        };
      });
  }, [selectedEventData, searchText]);

  // Show data according group
  const groupedEvents = useMemo(() => {
    return filteredData.reduce((acc, game) => {
      const key = game.eventTypeName;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(game);

      return acc;
    }, {});
  }, [filteredData]);

  return (
    <>
      <SubHeading isAddBtn={false} />
      <div className="tab-menu-sport">
        <div className="march-heading">
          <h2>Matches</h2>

          <div className="march_weekly">
            <div className="date-input-box">
              <CustomSelect
                className="select-control"
                value={
                  GAME_FILTER_OPTIONS.find(
                    (option) => option?.value === filterParams,
                  ) || GAME_FILTER_OPTIONS[0]
                }
                onChange={(selected) =>
                  setFilterParams(selected?.value || "All")
                }
                options={GAME_FILTER_OPTIONS}
                placeholder="Select Filter"
                isSearchable={false}
                aria-label="Match filter"
              />
            </div>
            <div className="search_box">
              <SearchInput
                value={searchText}
                onSearchChange={handleSearch}
              />

              {Boolean(searchText?.trim()) && (
                <button
                  type="button"
                  className="close_icon"
                  onClick={() => handleSearch("")}
                >
                  <Icon name="X" size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="box-sport">
          <Tabs
            id="controlled-tab-example game-setting-flex"
            activeKey={activeKey}
            onSelect={handleTabChange}
            className="tab_bottle"
          >
            {dashboardInitialData?.eventTypes?.map((option) => {
              const iconName =
                SPORTS_ICON[option?.eventTypeName?.toLowerCase()] || "Dot";
              return (
                <Tab
                  key={option?.eventTypeId}
                  eventKey={option?.eventTypeId}
                  title={
                    <>
                      <Icon className="icon_sport" size={20} name={iconName} />
                      <span>{option?.eventTypeName}</span>
                      <span className="game_count">{option?.eventCount}</span>
                    </>
                  }
                />
              );
            })}
          </Tabs>
          {Object.entries(groupedEvents).map(([eventTypeName, games]) => (
            <div className="sport-events-main" key={eventTypeName}>
              <div className="heading_events-title">
                <h3 className="sport-events-title">{eventTypeName}</h3>
              </div>

              {games.map((game) => {
                return (
                  <div
                    key={game.eventId}
                    className="mach-price-card cursor-pointer"
                    onClick={() => navigate(`/match-details/${game.eventId}`)}
                  >
                    <div className="maine_match_card">
                      <div className="mech_name_box">
                        <div className="mach_icon">
                          {(() => {
                            const teams =
                              game.eventName?.split(/ v | vs /i) || [];

                            const firstLatter =
                              teams[0]?.trim()?.charAt(0)?.toUpperCase() || "";
                            const secondLatter =
                              teams[1]?.trim()?.charAt(0)?.toUpperCase() || "";

                            return (
                              <>
                                <span>{firstLatter}</span>
                                <span>{secondLatter}</span>
                              </>
                            );
                          })()}
                        </div>
                        <div className="tournament_match_text">
                          <h2 className="mach_name">{game.eventName}</h2>
                          <p className="event_live">
                            <span className="live_match">
                              {game.inPlay ? "In Play" : ""}
                            </span>
                            <span className="date_text">
                              {DateUtils.fromUTC(game.eventOpenOn)}|{" "}
                              {game.competitionName}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="team-all-price">
                        <MatchOdds marketId={game.marketId} />
                      </div>

                      {/* <div className="line-symbol-main">
                                  <span className="symbol_box">B</span>
                                  <span className="symbol_box f_icon">F</span>
                                </div> */}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
